import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ChatText, ShieldCheck, Tray, Flame, Bank, Clipboard, PaperPlaneTilt, Wallet, Key, ArrowRight,
} from '@phosphor-icons/react';

/* Each card = { title, desc, Icon, iconBg, iconColor, go, show } */
function CardTile({ card, onClick }) {
  const { Icon } = card;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:border-emerald-500 hover:shadow-md transition-all flex items-start gap-4 relative"
      data-testid={`chat-home-${card.key}`}
    >
      <div
        className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${card.iconBg}`}
      >
        <Icon size={20} weight="bold" className={card.iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <h3 className="text-lg font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
            {card.title}
          </h3>
          <ArrowRight size={14} weight="bold" className="opacity-0 group-hover:opacity-100 text-emerald-600 transition-opacity" />
        </div>
        <p className="text-xs text-gray-500 mt-1 leading-snug">{card.desc}</p>
      </div>
    </button>
  );
}

export default function ChatHomePage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const role = user?.role || '';
  const isAdmin = ['admin', 'owner'].includes(role);
  const isPrivileged = ['admin', 'owner', 'assistant', 'accountant'].includes(role);

  const cards = [
    {
      key: 'chat', title: 'Chat', desc: 'Group messages, send LPG codes, see live complaints.',
      Icon: ChatText, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-700',
      go: () => nav('/chat/messages'),
      show: true,
    },
    {
      key: 'admin', title: 'Admin Panel', desc: 'Users, groups, staff sheets, backups, integrations.',
      Icon: ShieldCheck, iconBg: 'bg-gray-900', iconColor: 'text-white',
      go: () => nav('/'),
      show: isPrivileged,
    },
    {
      key: 'complaints', title: 'Complaint Box', desc: 'Saari pending complaints date-wise — view & resolve.',
      Icon: Tray, iconBg: 'bg-rose-100', iconColor: 'text-rose-700',
      go: () => nav('/complaints-hub'),
      show: true,
    },
    {
      key: 'lpg-report', title: 'LPG Report', desc: 'Daily ledger of sent / done / pending / billable codes.',
      Icon: Flame, iconBg: 'bg-amber-100', iconColor: 'text-amber-700',
      go: () => nav('/reports'),
      show: isPrivileged,
    },
    {
      key: 'bank', title: 'Bank Details', desc: 'Active accounts + QR codes + daily deposit sheet.',
      Icon: Bank, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-700',
      go: () => nav('/bank-statements'),
      show: isPrivileged,
    },
    {
      key: 'bulk-paste', title: 'Bulk Paste Dispatcher', desc: 'Multi-agency paste — auto route DAC codes to each group.',
      Icon: Clipboard, iconBg: 'bg-teal-100', iconColor: 'text-teal-700',
      go: () => nav('/chat/messages?action=bulk-paste'),
      show: isPrivileged,
    },
    {
      key: 'bulk-resend', title: 'Bulk Resend Paste', desc: 'Bharat Gas only · rows staff-attributed, agency posted real-time.',
      Icon: PaperPlaneTilt, iconBg: 'bg-amber-200', iconColor: 'text-amber-800',
      go: () => nav('/booking-files'),
      show: isPrivileged,
    },
    {
      key: 'wallet', title: 'Wallet & Ledger', desc: 'Agency + staff ledger, rate master, settlements — admin only.',
      Icon: Wallet, iconBg: 'bg-amber-200', iconColor: 'text-amber-800',
      go: () => nav('/chat/ledger'),
      show: isPrivileged,
    },
    {
      key: 'delivery-logins', title: 'Delivery Logins', desc: 'Indane agencies ki delivery-man user id · password one page.',
      Icon: Key, iconBg: 'bg-lime-100', iconColor: 'text-lime-800',
      go: () => nav('/distributors'),
      show: isPrivileged,
    },
  ].filter(c => c.show);

  return (
    <div className="min-h-full" data-testid="chat-home-page">
      <div className="mb-6">
        <div className="text-xs tracking-widest text-gray-500 font-semibold mb-2">HOME</div>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
          Welcome back, {user?.name || 'User'}
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          Choose where to go next. Top-left logo ko kabhi bhi click karke yahan wapas aa sakte hain.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(c => (
          <CardTile key={c.key} card={c} onClick={c.go} />
        ))}
      </div>
    </div>
  );
}
